import type {
  BaseQueryFn,
  CreateApiOptions,
  EndpointDefinitions,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/dist/query/react';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { baseQueryCasing } from './baseQueryCasing';

type BaseQuery = BaseQueryFn<FetchArgs, any, FetchBaseQueryError>;

export interface BaseApiConfig<
  Definitions extends EndpointDefinitions,
  TagTypes extends string,
> extends Pick<
    CreateApiOptions<BaseQuery, Definitions, string, TagTypes>,
    'endpoints' | 'tagTypes'
  > {
  name: string;
}

export interface BaseApiOptions {
  transformCasing: boolean;
}

export const createBaseApi = <
  Definitions extends EndpointDefinitions,
  TagTypes extends string,
>(
  { name, endpoints, tagTypes }: BaseApiConfig<Definitions, TagTypes>,
  options: BaseApiOptions = { transformCasing: true }
) => {
  let baseQuery: BaseQuery;

  const api = createApi({
    reducerPath: name,
    baseQuery: (fetchArgs: FetchArgs, api, extraOptions) => {
      if (!baseQuery) {
        throw new Error(`baseQuery was not initialized for ${name}`);
      }

      if (options?.transformCasing) {
        return baseQueryCasing(baseQuery)(fetchArgs, api, extraOptions);
      }

      return baseQuery(fetchArgs, api, extraOptions);
    },
    tagTypes,
    endpoints,
  });

  const initApi = (bq: typeof baseQuery) => {
    baseQuery = bq;

    return api;
  };

  return [api, initApi] as const;
};
